import { Typography, TextField, Button, CircularProgress, Stack, MenuItem } from "@mui/material";
import { CadastrarClienteForm } from "./styles";
import { useMascara } from "../../../core/hooks/useMascara";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { clienteSchema } from "../../schemas/clienteSchema";
import { FieldValues } from "react-hook-form/dist/types";
import { useEffect } from "react";
import { useClientes } from "../../hooks/useClientes";
import { ICliente, IndicadorInscricaoEstadual, IndicadorInscricaoEstadualDescricao, TipoPessoaCliente } from "../../interface/ICliente";

interface CadastrarClienteProps {
  dadosCliente?: ICliente;
  cadastrar: (cliente: FieldValues) => void;
  cancelar: () => void;
  loading: boolean;
  editar?: (cliente: FieldValues) => void;
}

export const CadastrarEditarCliente = ({ cadastrar, editar, dadosCliente, cancelar, loading }: CadastrarClienteProps) => {
  const { mascaraCPF, mascaraCNPJ, mascaraTelefone } = useMascara();
  const { setValue, register, handleSubmit, watch, formState: { errors } } = useForm<FieldValues>({
    resolver: yupResolver(clienteSchema),
    mode: "onChange",
    defaultValues: {
      tipoPessoa: TipoPessoaCliente.FISICA,
      indicadorInscricaoEstadual: IndicadorInscricaoEstadual.NAO_CONTRIBUINTE,
    }
  });

  const nomeValue = watch("nome");
  const cpfValue = watch("cpf")
  const telefoneValue = watch("telefone")
  const cnpjValue = watch("cnpj")
  const tipoPessoaValue = watch("tipoPessoa")
  const indicadorInscricaoEstadualValue = watch("indicadorInscricaoEstadual")

  useEffect(() => {
    setValue("cpf", mascaraCPF(cpfValue))
  },[cpfValue])

  useEffect(() => {
    setValue("telefone", mascaraTelefone(telefoneValue))
  },[telefoneValue])

  useEffect(() => {
    setValue("cnpj", mascaraCNPJ(cnpjValue))
  },[cnpjValue])

  useEffect(() => {
    if(dadosCliente) {
      const { telefone, nome, observacao, cpf, tipoPessoa, cnpj, razaoSocial, inscricaoEstadual, indicadorInscricaoEstadual } = dadosCliente;
      if(nome) {
        setValue("nome", nome);
      }

      if(telefone) {
        setValue("telefone", mascaraTelefone(telefone))
      }

      if(observacao) {
        setValue("observacao", observacao);
      }

      if(cpf) {
        setValue("cpf", mascaraCPF(cpf));
      }

      setValue("tipoPessoa", tipoPessoa ?? TipoPessoaCliente.FISICA);
      setValue("indicadorInscricaoEstadual", indicadorInscricaoEstadual ?? IndicadorInscricaoEstadual.NAO_CONTRIBUINTE);

      if(cnpj) {
        setValue("cnpj", mascaraCNPJ(cnpj));
      }

      if(razaoSocial) {
        setValue("razaoSocial", razaoSocial);
      }

      if(inscricaoEstadual) {
        setValue("inscricaoEstadual", inscricaoEstadual);
      }
    }
  }, [dadosCliente])

  const handleCancelar = () => {
    setValue("nome", "");
    setValue("telefone", "");
    setValue("cpf", "");
    setValue("observacao", "");
    setValue("cnpj", "");
    setValue("razaoSocial", "");
    setValue("inscricaoEstadual", "");
    setValue("tipoPessoa", TipoPessoaCliente.FISICA);
    setValue("indicadorInscricaoEstadual", IndicadorInscricaoEstadual.NAO_CONTRIBUINTE);
    cancelar();
  }

  const handleFormulario = async (cliente: FieldValues) => {
    if(dadosCliente?.uuid && editar) {
      return editar(cliente);
    }

    return cadastrar(cliente);
  }

  return (
    <CadastrarClienteForm onSubmit={handleSubmit(handleFormulario)}>
      <Typography variant="h6">Cadastro do cliente</Typography>
      <TextField
        label="Nome*"
        style={{ width: "100%", margin: "1rem 0" }}
        {...register("nome")}
        error={!!errors.nome}
        helperText={<>{errors.nome?.message}</>}
        autoFocus={!nomeValue}
      />
      <TextField
        label="Observação"
        style={{ width: "100%", margin: "1rem 0" }}
        {...register("observacao")}
        error={!!errors.observacao}
        helperText={<>{errors.observacao?.message}</>}
      />
      <TextField
        label="Telefone"
        style={{ width: "100%", margin: "1rem 0" }}
        {...register("telefone")}
        error={!!errors.telefone}
        helperText={<>{errors.telefone?.message}</>}
      />
      <TextField
        label="CPF"
        style={{ width: "100%", margin: "1rem 0" }}
        {...register("cpf")}
        error={!!errors.cpf}
        helperText={<>{errors.cpf?.message}</>}
      />

      <Typography variant="subtitle1" sx={{ width: "100%", marginTop: "0.5rem" }}>
        Dados fiscais (NF-e)
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ width: "100%", marginBottom: "0.5rem" }}>
        Necessários apenas para emitir NF-e para este cliente.
      </Typography>

      <TextField
        select
        label="Tipo de pessoa"
        style={{ width: "100%", margin: "1rem 0" }}
        value={tipoPessoaValue ?? TipoPessoaCliente.FISICA}
        {...register("tipoPessoa")}
      >
        <MenuItem value={TipoPessoaCliente.FISICA}>Pessoa física</MenuItem>
        <MenuItem value={TipoPessoaCliente.JURIDICA}>Pessoa jurídica</MenuItem>
      </TextField>

      {tipoPessoaValue === TipoPessoaCliente.JURIDICA && (
        <>
          <TextField
            label="Razão social"
            style={{ width: "100%", margin: "1rem 0" }}
            {...register("razaoSocial")}
            error={!!errors.razaoSocial}
            helperText={<>{errors.razaoSocial?.message}</>}
          />
          <TextField
            label="CNPJ*"
            style={{ width: "100%", margin: "1rem 0" }}
            {...register("cnpj")}
            error={!!errors.cnpj}
            helperText={<>{errors.cnpj?.message}</>}
          />
        </>
      )}

      <TextField
        select
        label="Indicador de inscrição estadual"
        style={{ width: "100%", margin: "1rem 0" }}
        value={indicadorInscricaoEstadualValue ?? IndicadorInscricaoEstadual.NAO_CONTRIBUINTE}
        {...register("indicadorInscricaoEstadual")}
      >
        {Object.values(IndicadorInscricaoEstadual).map(indicador => (
          <MenuItem key={indicador} value={indicador}>
            {IndicadorInscricaoEstadualDescricao[indicador]}
          </MenuItem>
        ))}
      </TextField>

      {indicadorInscricaoEstadualValue === IndicadorInscricaoEstadual.CONTRIBUINTE && (
        <TextField
          label="Inscrição estadual*"
          style={{ width: "100%", margin: "1rem 0" }}
          {...register("inscricaoEstadual")}
          error={!!errors.inscricaoEstadual}
          helperText={<>{errors.inscricaoEstadual?.message}</>}
        />
      )}

      <Stack flexDirection={"row"} sx={{ width: "100%" }} justifyContent={"space-between"}>
        <Button
          variant="outlined"
          style={{ width: "47%" }}
          type="button"
          onClick={handleCancelar}
        >
          CANCELAR
        </Button>
        <Button
          variant="contained"
          style={{ width: "47%" }}
          type="submit"
        >
          {loading == true ? <CircularProgress color="inherit" /> : (
            dadosCliente?.uuid ? "EDITAR" : "CADASTRAR"
          )}
        </Button>
      </Stack>
    </CadastrarClienteForm>
  );
};
